// Pure, dependency-free rules for the Agreements module (convenios de la
// Dirección de Extensión). Kept separate from the React components and the
// Cloud Functions so it can be unit-tested directly.
// gfunctions/extension/agreements/_shared/agreementLogic.js mirrors this logic (separate
// Node deployment, cannot import from src/), so keep the two in sync when
// changing a rule.
//
// Validation returns English error CODES, not messages: the Spanish text lives
// in ./agreementLabels.js, which also renders the codes the backend returns —
// so a rule checked on both sides reads the same either way.
import { AGREEMENT_STATUS, AGREEMENT_TYPE } from "../../constants";

export const AGREEMENT_TYPES = Object.values(AGREEMENT_TYPE);
export const AGREEMENT_STATUSES = Object.values(AGREEMENT_STATUS);

// Dates are stored as date-only ISO strings ("YYYY-MM-DD") rather than
// Timestamps: a convenio's plazo is a calendar fact, and a Timestamp would land
// on a different day depending on the reader's timezone. As a bonus they sort
// and range-query lexicographically, so a date filter needs no conversion.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDate = (value) => {
  if (typeof value !== "string" || !DATE_RE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  if (isNaN(d.getTime())) return false;
  // Rejects the shapes Date silently rolls over (2026-02-31 → 2026-03-03).
  return d.toISOString().slice(0, 10) === value;
};

// The institutional code doubles as the document id, so it has to survive being
// a Firestore key: no slashes, no surrounding blanks, and case-insensitive
// identity (DEX-001 and dex-001 are the same convenio).
export const normalizeCode = (code) =>
  String(code == null ? "" : code)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ")
    .replace(/\//g, "-");

// Money arrives from a form, so it may be a string. Anything non-finite becomes
// NaN and fails validation rather than silently landing in Firestore as 0.
export const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.replace(/,/g, ""));
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Budget rollup (design doc Properties 15 and 16): remaining is what is left to
// commit, percentage is how much of the convenio is already spoken for. A
// convenio with no value reports 0% instead of dividing by zero.
export const deriveBudget = ({ totalValue, committedValue } = {}) => {
  const total = toNumber(totalValue);
  const committed = toNumber(committedValue);
  const safeTotal = Number.isNaN(total) ? 0 : total;
  const safeCommitted = Number.isNaN(committed) ? 0 : committed;
  return {
    remainingValue: round2(safeTotal - safeCommitted),
    committedPercentage: safeTotal > 0 ? round2((safeCommitted / safeTotal) * 100) : 0,
  };
};

// Validates the fields a gestor can send. With `partial` (an update) only the
// keys actually present are checked — except for the cross-field rules, which
// need both sides, so callers must merge the stored document into `input`
// before validating an update.
//
// Returns { valid, errors } where errors maps field → English code.
export const validateAgreement = (input = {}, { partial = false } = {}) => {
  const errors = {};
  const has = (key) => !partial || Object.prototype.hasOwnProperty.call(input, key);

  if (has("code") && !normalizeCode(input.code)) errors.code = "CODE_REQUIRED";

  if (has("subject") && String(input.subject || "").trim().length < 10) {
    errors.subject = "SUBJECT_TOO_SHORT";
  }

  // Todo convenio apunta a una entidad REGISTRADA: el nombre de la contratante
  // dejó de escribirse a mano y ahora se escoge del registro de entidades. Lo
  // que se valida aquí es que se haya escogido una; que exista de verdad lo
  // comprueba el servidor al leerla dentro de la transacción, porque el cliente
  // no puede garantizar que siga existiendo cuando llegue la escritura.
  //
  // `contractingEntity` sobrevive como la razón social DENORMALIZADA —la escribe
  // el servidor desde el documento de la entidad, nunca el cliente—, así que no
  // se valida: en un alta todavía no existe cuando se pasa por aquí.
  if (has("entityNit") && !String(input.entityNit || "").trim()) {
    errors.entityNit = "ENTITY_REQUIRED";
  }

  if (has("type") && !AGREEMENT_TYPES.includes(input.type)) errors.type = "INVALID_TYPE";

  if (has("status") && !AGREEMENT_STATUSES.includes(input.status)) {
    errors.status = "INVALID_STATUS";
  }

  const startOk = isValidDate(input.startDate);
  const endOk = isValidDate(input.endDate);
  if (has("startDate") && !startOk) errors.startDate = "INVALID_START_DATE";
  if (has("endDate") && !endOk) errors.endDate = "INVALID_END_DATE";
  // Property 5: the end date must be strictly after the start date. Only
  // checkable once both parse, so a malformed date reports its own error first.
  if (startOk && endOk && !(input.endDate > input.startDate)) {
    errors.endDate = "END_NOT_AFTER_START";
  }

  // Property 6: the value must be a positive number.
  const total = toNumber(input.totalValue);
  if (has("totalValue") && (Number.isNaN(total) || total <= 0)) {
    errors.totalValue = "VALUE_NOT_POSITIVE";
  }

  // Committed value is entered by hand in this phase (derived contracts are not
  // linked to the registry yet), so it is bounded here rather than computed:
  // never negative, never above the convenio's own value (Property 17).
  const committedGiven =
    has("committedValue") &&
    input.committedValue !== null &&
    input.committedValue !== undefined &&
    input.committedValue !== "";
  if (committedGiven) {
    const committed = toNumber(input.committedValue);
    if (Number.isNaN(committed) || committed < 0) {
      errors.committedValue = "COMMITTED_NEGATIVE";
    } else if (!Number.isNaN(total) && total > 0 && committed > total) {
      errors.committedValue = "COMMITTED_EXCEEDS_TOTAL";
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

// Días que le quedan de vigencia al convenio respecto de hoy. Negativo = ya
// venció. Sólo para mostrar: no participa en ninguna validación.
export const daysUntilEnd = (endDate, today = new Date()) => {
  if (!isValidDate(endDate)) return null;
  const end = new Date(`${endDate}T00:00:00Z`);
  const from = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  return Math.round((end - from) / 86400000);
};
